/**
 * Assistant panel content (frontend-display.md §4.2 / frontend-ux-design.md §4.3).
 *
 * Composes the rendered surface — context switcher header, scrollable message
 * list, composer — on the rail's paper (E0: no nested boxes except the tool
 * card). LIVE: backed by `useAssistantChat` against `POST /api/assistant/chat`
 * (SSE), per scope. Tool reads surface as chips, `propose_transition` surfaces
 * an Approve/Reject card wired to the proposals write path.
 */
import * as React from "react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Icon } from "@/components/ui/icon";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { ContextSwitcher } from "@/components/assistant/ContextSwitcher";
import { MessageList } from "@/components/assistant/MessageList";
import { Composer } from "@/components/assistant/Composer";
import { useAssistantChat } from "@/components/assistant/use-assistant-chat";
import type { AssistantScope } from "@/components/assistant/types";

interface AssistantPanelProps {
  scope: AssistantScope;
  onScopeChange: (scope: AssistantScope) => void;
  /** Close affordance (the rail header X / Sheet close). */
  onClose: () => void;
}

export function AssistantPanel({
  scope,
  onScopeChange,
  onClose,
}: AssistantPanelProps) {
  const {
    messages,
    isStreaming,
    send: onSend,
    stop: onStop,
    clear: onClear,
    approve: onApprove,
    reject: onReject,
  } = useAssistantChat(scope);

  // Show the typing indicator only while waiting for the first streamed part of
  // the in-flight assistant turn (the last message is an empty assistant bubble).
  const last = messages[messages.length - 1];
  const awaitingReply =
    isStreaming &&
    last?.role === "assistant" &&
    last.parts.length === 0;

  return (
    <div
      className="flex h-full flex-col bg-background"
      data-testid="assistant-panel"
    >
      {/* Header: brand glyph + title + "Shell" tag + close, with the context
          switcher on its own row. A single border-b grounds the chrome. */}
      <div className="shrink-0 border-b border-border px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Icon name="assistantChat" size={16} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-none">
              Assistant
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label="Close assistant"
            data-testid="assistant-close"
            onClick={onClose}
          >
            <Icon name="close" size={16} aria-hidden />
          </Button>
        </div>
        <div className="mt-1.5">
          <ContextSwitcher scope={scope} onScopeChange={onScopeChange} />
        </div>
      </div>

      {/* Message list — centered reading column so the wide rail stays legible. */}
      <ScrollArea className="flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 py-4">
          {messages.length === 0 ? (
            <Empty className="py-10">
              <EmptyHeader>
                <EmptyTitle>No messages yet</EmptyTitle>
                <EmptyDescription>
                  Ask about an entity, request a change, or run a search. Mutations
                  surface an Approve / Reject card before anything is written.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <MessageList
              messages={messages}
              onApprove={onApprove}
              onReject={onReject}
              isStreaming={awaitingReply}
            />
          )}
        </div>
      </ScrollArea>

      {/* Composer — same centered column so it lines up with the messages. */}
      <div className="mx-auto w-full max-w-2xl">
        <Composer
          onSend={onSend}
          onStop={onStop}
          onClear={onClear}
          isStreaming={isStreaming}
          disabled={isStreaming}
        />
      </div>
    </div>
  );
}

export default AssistantPanel;
